import type {AppLocale} from "@/src/modules/core/i18n";
import {getIntlLocaleTag} from "@/src/modules/core/i18n/day-picker-locale";

/** API value: registration sticker expiration month (`YYYY-MM`). */
export const PLATE_EXPIRES_ON_PATTERN = /^\d{4}-\d{2}$/;

const LEGACY_PLATE_EXPIRES_ON_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function isPlateExpiresOnValue(value: string): boolean {
  return PLATE_EXPIRES_ON_PATTERN.test(value);
}

/** Normalize API / draft strings (accepts legacy `YYYY-MM-DD`). */
export function normalizePlateExpiresOn(value: unknown): string | null {
  if (typeof value !== "string") return null;
  if (PLATE_EXPIRES_ON_PATTERN.test(value)) return value;
  if (LEGACY_PLATE_EXPIRES_ON_PATTERN.test(value)) return value.slice(0, 7);
  return null;
}

export function formatPlateExpiresOn(value: string, locale: AppLocale): string {
  const normalized = normalizePlateExpiresOn(value);
  if (!normalized) return value;

  const [year, month] = normalized.split("-");
  const monthNum = Number.parseInt(month, 10);
  if (!year || monthNum < 1 || monthNum > 12) return value;

  if (locale === "es") {
    return `${month.padStart(2, "0")}/${year}`;
  }

  const date = new Date(Number.parseInt(year, 10), monthNum - 1, 1);
  return date.toLocaleString(getIntlLocaleTag(locale), {month: "short", year: "numeric"});
}

export function parsePlateExpiresOnParts(value: string | null | undefined): {
  year: string | null;
  month: string | null;
} {
  const normalized = normalizePlateExpiresOn(value ?? "");
  if (!normalized) return {year: null, month: null};
  const [year, month] = normalized.split("-");
  return {year, month};
}

export function buildPlateExpiresOn(year: string, month: string): string {
  return `${year}-${month.padStart(2, "0")}`;
}
